import { useState } from 'react';
import { ArrowRightLeft, Clock, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { useThemeStore } from '../../lib/themeStore';
import { GroupedTrade } from '../../lib/utils/tradeUtils';
import { PlayerTradeInfo } from '../Messages/TradeOffers/PlayerTradeInfo';

// Composant local pour l'affichage des draft picks
const DraftPickInfo = ({ description, originalUsername }: { description: string; originalUsername?: string | null }) => {
  const { theme } = useThemeStore();
  
  return (
    <div className="d-flex align-items-center p-3 rounded-3 border" style={{
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(168, 85, 247, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
      borderColor: theme === 'dark' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.2)'
    }}>
      <Calendar size={20} className="text-purple me-3" style={{ color: '#a855f7' }} />
      <div className="flex-grow-1">
        <div className={`fw-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
          {description}
        </div>
        {originalUsername && (
          <div className="small text-muted">
            Choix original: {originalUsername}
          </div>
        )}
      </div>
    </div>
  );
};

interface TradeHistoryCardProps {
  trade: GroupedTrade;
  language: string;
  formatDate: (date: string) => string;
}

export function TradeHistoryCard({ trade, language, formatDate }: TradeHistoryCardProps) {
  const { theme } = useThemeStore();
  const [isExpanded, setIsExpanded] = useState(false);

  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculer les totaux des salaires
  const calculateSalaryTotals = () => {
    let senderTotal = 0;
    let receiverTotal = 0;

    trade.trades.forEach(tradePair => {
      if (tradePair.sender_salary) senderTotal += tradePair.sender_salary;
      if (tradePair.sender_goalie_salary) senderTotal += tradePair.sender_goalie_salary;
      if (tradePair.receiver_salary) receiverTotal += tradePair.receiver_salary;
      if (tradePair.receiver_goalie_salary) receiverTotal += tradePair.receiver_goalie_salary;
    });

    return { senderTotal, receiverTotal };
  };

  const { senderTotal, receiverTotal } = calculateSalaryTotals();

  return (
    <div className="card border-0 shadow-sm mb-3" style={{
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
        : 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)',
      backdropFilter: 'blur(10px)'
    }}>
      <div className="card-body p-4">
        {/* Header de l'échange */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <ArrowRightLeft size={20} className="text-success me-2" />
            <div>
              <h6 className={`mb-1 fw-bold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                {trade.sender_username} ↔ {trade.receiver_username}
              </h6>
              <div className="d-flex align-items-center text-muted small">
                <Clock size={14} className="me-1" />
                {formatDate(trade.time_stamp)}
              </div>
            </div>
          </div>
          
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span className="ms-1 d-none d-md-inline">
              {isExpanded ? 'Réduire' : 'Détails'}
            </span>
          </button>
        </div>

        {/* Résumé de l'échange */}
        <div className="row">
          <div className="col-md-5">
            <div className="text-center p-3 rounded" style={{
              backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              <h6 className="text-primary fw-bold mb-2">{trade.sender_username}</h6>
              <div className="small text-muted">
                <div>
                  {trade.trades.filter(t => t.sender_player_name || t.sender_goalie_name || t.sender_pick_description).length} élément{trade.trades.filter(t => t.sender_player_name || t.sender_goalie_name || t.sender_pick_description).length > 1 ? 's' : ''}
                </div>
                {senderTotal > 0 && (
                  <div className="mt-1">
                    Total: {formatCurrency(senderTotal)}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="col-md-2 d-flex align-items-center justify-content-center">
            <ArrowRightLeft size={24} className="text-success" />
          </div>
          
          <div className="col-md-5">
            <div className="text-center p-3 rounded" style={{
              backgroundColor: theme === 'dark' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <h6 className="text-success fw-bold mb-2">{trade.receiver_username}</h6>
              <div className="small text-muted">
                <div>
                  {trade.trades.filter(t => t.receiver_player_name || t.receiver_goalie_name || t.receiver_pick_description).length} élément{trade.trades.filter(t => t.receiver_player_name || t.receiver_goalie_name || t.receiver_pick_description).length > 1 ? 's' : ''}
                </div>
                {receiverTotal > 0 && (
                  <div className="mt-1">
                    Total: {formatCurrency(receiverTotal)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Détails de l'échange (collapsible) */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-top">
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-primary fw-bold mb-3">
                  Envoyés par {trade.sender_username}
                </h6>
                <div className="trade-items">
                  {trade.trades.map((tradePair, index) => (
                    <div key={index}>
                      {/* Joueur du sender */}
                      {tradePair.sender_player_name && (
                        <div className="mb-3">
                          <PlayerTradeInfo
                            name={tradePair.sender_player_name}
                            goals={tradePair.sender_goals}
                            assists={tradePair.sender_assists}
                            points={tradePair.sender_points}
                            salary={tradePair.sender_salary}
                            yearleft={tradePair.sender_yearleft}
                            isNHL={tradePair.sender_isnhl || false}
                            language={language as "fr" | "en"}
                          />
                        </div>
                      )}
                      
                      {/* Gardien du sender */}
                      {tradePair.sender_goalie_name && (
                        <div className="mb-3">
                          <PlayerTradeInfo
                            isGoalie={true}
                            name={tradePair.sender_goalie_name}
                            wins={tradePair.sender_goalie_wins}
                            savepct={tradePair.sender_goalie_savepct}
                            shutouts={tradePair.sender_goalie_shutout}
                            salary={tradePair.sender_goalie_salary}
                            yearleft={tradePair.sender_goalie_yearleft}
                            language={language as "fr" | "en"}
                          />
                        </div>
                      )}
                      
                      {/* Draft pick du sender */}
                      {tradePair.sender_pick_description && (
                        <div className="mb-3">
                          <DraftPickInfo
                            description={tradePair.sender_pick_description}
                            originalUsername={tradePair.sender_pick_original_username}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="col-md-6">
                <h6 className="text-success fw-bold mb-3">
                  Envoyés par {trade.receiver_username}
                </h6>
                <div className="trade-items">
                  {trade.trades.map((tradePair, index) => (
                    <div key={index}>
                      {/* Joueur du receiver */}
                      {tradePair.receiver_player_name && (
                        <div className="mb-3">
                          <PlayerTradeInfo
                            name={tradePair.receiver_player_name}
                            goals={tradePair.receiver_goals}
                            assists={tradePair.receiver_assists}
                            points={tradePair.receiver_points}
                            salary={tradePair.receiver_salary}
                            yearleft={tradePair.receiver_yearleft}
                            isNHL={tradePair.receiver_isnhl || false}
                            language={language as "fr" | "en"}
                          />
                        </div>
                      )}
                      
                      {/* Gardien du receiver */}
                      {tradePair.receiver_goalie_name && (
                        <div className="mb-3">
                          <PlayerTradeInfo
                            isGoalie={true}
                            name={tradePair.receiver_goalie_name}
                            wins={tradePair.receiver_goalie_wins}
                            savepct={tradePair.receiver_goalie_savepct}
                            shutouts={tradePair.receiver_goalie_shutout}
                            salary={tradePair.receiver_goalie_salary}
                            yearleft={tradePair.receiver_goalie_yearleft}
                            language={language as "fr" | "en"}
                          />
                        </div>
                      )}
                      
                      {/* Draft pick du receiver */}
                      {tradePair.receiver_pick_description && (
                        <div className="mb-3">
                          <DraftPickInfo
                            description={tradePair.receiver_pick_description}
                            originalUsername={tradePair.receiver_pick_original_username}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
